/* eslint-disable  no-nested-ternary */
/* eslint-disable react/no-unstable-nested-components */
import {
    ExpandedState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    getExpandedRowModel,
    Row,
    SortingState,
    useReactTable,
} from '@tanstack/react-table';
import React, {HTMLProps, useEffect} from 'react';

import {Button} from '../Button/button';
import {Skeleton} from '../skeleton';
import {Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow} from './TablePrimitives';
import {TableProps} from './types';

function IndeterminateCheckbox({
    indeterminate,
    className = '',
    ...rest
}: {indeterminate?: boolean} & HTMLProps<HTMLInputElement>) {
    const ref = React.useRef<HTMLInputElement>(null!);

    React.useEffect(() => {
        if (typeof indeterminate === 'boolean') {
            ref.current.indeterminate = !rest.checked && indeterminate;
        }
    }, [ref, indeterminate]); // eslint-disable-line react-hooks/exhaustive-deps

    return <input type='checkbox' ref={ref} className={`${className} cursor-pointer`} {...rest} />;
}

export function SimpleTable<TData, TValue>({
    columns,
    data,
    loading,
    pagination,
    filterValue,
    enableMultiRowSelection,
}: any) {
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [globalFilter, setGlobalFilter] = React.useState('');
    const [expanded, setExpanded] = React.useState<ExpandedState>({});

    const columnsToUse = React.useMemo(() => {
        if (!enableMultiRowSelection) return [...columns];

        return [
            {
                id: 'select',
                // header: ({ table }) => (
                //   <div className='px-1'></div>
                // ),
                cell: ({row}: {row: Row<TData>}) => (
                    <IndeterminateCheckbox
                        {...{
                            checked: row.getIsSelected(),
                            disabled: !row.getCanSelect(),
                            indeterminate: row.getIsSomeSelected(),
                            onChange: row.getToggleSelectedHandler(),
                        }}
                    />
                ),
            },
            ...columns,
        ];
    }, [columns, enableMultiRowSelection]);

    const {pageIndex, pageSize, pageCount, onChange} = pagination || {};
    const table = useReactTable({
        data,
        columns: columnsToUse,
        getCoreRowModel: getCoreRowModel(),
        pageCount: pageCount ?? -1,
        manualPagination: true,
        onPaginationChange: onChange,
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            sorting,
            globalFilter,
            expanded,
        },
        onExpandedChange: setExpanded,
        getSubRows: (row: any) => row.subRows,
        globalFilterFn: (row, columnId, filterText) => {
            const safeValue = (() => {
                const value = row.getValue(columnId);
                return typeof value === 'number' ? String(value) : value;
            })() as string;
            return safeValue?.toLowerCase().includes(filterText.toLowerCase());
        },
        onGlobalFilterChange: setGlobalFilter,
    });

    useEffect(() => {
        if (filterValue) {
            setGlobalFilter(filterValue);
        }
    }, [filterValue]);

    // @ts-ignore
    return (
        <Table>
            <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                            <TableHead key={header.id} colSpan={header.colSpan}>
                                {header.isPlaceholder
                                    ? null
                                    : flexRender(header.column.columnDef.header, header.getContext())}
                            </TableHead>
                        ))}
                    </TableRow>
                ))}
            </TableHeader>
            <TableBody>
                {table.getRowModel().rows.map((row) => {
                    return (
                        <TableRow key={row.id}>
                            {row.getVisibleCells().map((cell) => {
                                return (
                                    <TableCell key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    );
                })}
            </TableBody>
            {pagination && !loading ? (
                <TableFooter>
                    <TableRow>
                        <TableCell colSpan={columnsToUse.length} className='text-center'>
                            <Button
                                variant='outline'
                                className='mr-2'
                                onClick={() => table.previousPage()}
                                disabled={pageIndex === 0}>
                                Previous
                            </Button>
                            <Button
                                variant='outline'
                                onClick={() => table.nextPage()}
                                disabled={pageCount === pageIndex}>
                                Next
                            </Button>
                        </TableCell>
                    </TableRow>
                </TableFooter>
            ) : null}
        </Table>
    );
}
